import React from 'react';
import { FileText, Download } from 'lucide-react';

const DECK_PATH = '/docs/anzenpostplus-overview.pdf';

// GA4 event helper. window.gtag が無い環境では何もしない。
const trackDeckDownload = (placement: string) => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { gtag?: (cmd: string, action: string, params: Record<string, unknown>) => void };
  if (typeof w.gtag === 'function') {
    w.gtag('event', 'download_deck', {
      event_category: 'lead',
      event_label: placement,
      placement,
    });
  }
};

type Variant = 'inline-link' | 'callout-card' | 'subtle-button' | 'hero-pill';

interface DeckDownloadCTAProps {
  /** GA4 と内部識別のため、設置箇所を一意に表す文字列 (例: 'landing-hero', 'pricing-top', 'lp-construction', 'blog-end-<slug>'). */
  placement: string;
  /** UI バリエーション. デフォルト 'callout-card'. */
  variant?: Variant;
  /** カード型のときに任意の補助テキスト. */
  description?: string;
}

export const DeckDownloadCTA: React.FC<DeckDownloadCTAProps> = ({
  placement,
  variant = 'callout-card',
  description,
}) => {
  const onClick = () => trackDeckDownload(placement);

  if (variant === 'inline-link') {
    // 最も控えめ. Hero 下などの「補助情報」位置に置く想定.
    return (
      <a
        href={DECK_PATH}
        target="_blank"
        rel="noopener"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-safety-600 underline-offset-4 hover:underline transition"
      >
        <FileText size={14} />
        概要資料 (PDF・14ページ) をダウンロード
      </a>
    );
  }

  if (variant === 'hero-pill') {
    // Hero 直下向けの目立つピル. ブランドオレンジの淡色塗り + 枠 + シャドウで、
    // 1次CTA(濃オレンジ塗り) と色相を共有しつつ役割を分けて視認性を上げる.
    return (
      <a
        href={DECK_PATH}
        target="_blank"
        rel="noopener"
        onClick={onClick}
        data-cta="deck"
        className="inline-flex items-center gap-2.5 hover:-translate-y-0.5 transition"
        style={{
          padding: '11px 20px',
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 700,
          color: '#c2410c',
          textDecoration: 'none',
          background: '#fff7ed',
          border: '1.5px solid #fdba74',
          boxShadow: '0 6px 16px -8px rgba(249,115,22,.4)',
        }}
      >
        <Download size={17} />
        概要資料をダウンロード
        <span style={{ fontSize: 12, fontWeight: 600, color: '#ea580c', opacity: 0.8 }}>PDF・A4 14p</span>
      </a>
    );
  }

  if (variant === 'subtle-button') {
    // 中サイズ. インディゴ塗りで「PDF」を視認しやすく. オレンジの 1次CTA と色相が違うのでカニバリしない.
    return (
      <a
        href={DECK_PATH}
        target="_blank"
        rel="noopener"
        onClick={onClick}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white border border-indigo-600 text-base font-bold rounded-xl shadow-md shadow-indigo-500/30 hover:bg-indigo-700 hover:border-indigo-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/40 transition"
      >
        <Download size={18} />
        資料をダウンロード (PDF)
      </a>
    );
  }

  // callout-card: 中規模のセクション末尾向け. カード本体は中性 (slate-50), DLボタンは indigo 塗り.
  return (
    <aside
      className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-7"
      aria-label="概要資料ダウンロード"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className="flex items-start gap-3 flex-1">
          <div className="shrink-0 w-12 h-12 bg-white border border-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
            <FileText size={24} />
          </div>
          <div>
            <h3 className="text-base font-bold text-industrial-900">
              社内ご検討用の概要資料 (PDF・A4 14ページ)
            </h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              {description ?? 'サービス概要・4M分析・料金・セキュリティを 1ファイルにまとめました。社内回覧や上長報告にどうぞ。'}
            </p>
          </div>
        </div>
        <a
          href={DECK_PATH}
          target="_blank"
          rel="noopener"
          onClick={onClick}
          className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-lg shadow-md shadow-indigo-500/30 hover:bg-indigo-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/40 transition whitespace-nowrap"
        >
          <Download size={16} />
          ダウンロード
        </a>
      </div>
    </aside>
  );
};
